import React, {useMemo, useState, useEffect, useRef} from "react";
import { CircularProgress, Snackbar, Tooltip } from '@mui/material';
import { EssayHelper } from "../helpers/essay.helper";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Autocomplete, Avatar, Box, Button, Chip, Container, FormControl, Grid, IconButton, InputLabel, Paper, 
  List, ListItem, ListItemAvatar, ListItemButton, ListItemIcon, ListItemText, MenuItem, 
  Divider, Select, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography } from '@mui/material';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PublishedWithChangesIcon from '@mui/icons-material/PublishedWithChanges';
import { AppHelper } from '../AppHelper';
import AppTopBar from './common/app-top-bar';
import { useEssayContext } from "../contexts/context-essay";
import InfoIcon from "@mui/icons-material/Info";
import { COUNTRY_OPTIONS } from '../constants/countries';

export default function PageProfile() {
  const navigate = useNavigate();
  const [oUser, setOUser] = useState(AppHelper.userCacheGet());
  const [gradeOK, setGradeOK] = useState(true);
  const [grade, setGrade] = useState((oUser && oUser.grade) ? oUser.grade : 'P5');
  const [profiles, setProfiles] = useState([]);
  const [isCreatingProfile, setIsCreatingProfile] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [newEmail1, setNewEmail1] = useState('');
  const [newEmail2, setNewEmail2] = useState('');
  
  const [gradeList, setGradeList] = useState([]);
  const [newProfileGrade, setNewProfileGrade] = useState('');
  const [updatedProfileId, setUpdatedProfileId] = useState('');
  const [loading, setLoading] = useState(false);  
  const [isChangingPicture, setIsChangingPicture] = useState(false);
  // const [selectedFile, setSelectedFile] = useState(null);
  const [isDefaultProfileUpdated, setIsDefaultProfileUpdated] = useState(false);
  const refTopBar = useRef(null);
  const [accountType, setAccountType] = useState(oUser.accountType || 'STU');
  const [email1, setEmail1] = useState(oUser.email1 || '');
  const [email2, setEmail2] = useState(oUser.email2 || '');
  const [isUpdatingAccountType, setIsUpdatingAccountType] = useState(false);
  const {essayList, isEssayListLoaded, setIsEssayListLoaded, loadEssayList, essaySelect} = useEssayContext();
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const isMyProfile = profiles.find(p => p.profileId === oUser.profileId);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [countryCode, setCountryCode] = useState(oUser?.countryCode || 'Singapore');
  const [locale, setLocale] = useState(oUser?.locale || 'en-UK');

  const accountTypeList = [
    { v: 'STU', n: 'STUDENT' },
    { v: 'TEA', n: 'TUTOR' },
    { v: 'PAR', n: 'PARENT' },
	  { v: 'TUI', n: 'TUITION CENTER' },
  ];

  const localeOptions = [
    { value: 'en-UK', label: 'UK English'},
    { value: 'en-US', label: 'US English'},
  ]
  
  const AVATAR_OPTIONS = [
    "image_0_0.png",
    "image_0_1.png",
    "image_0_2.png",
    "image_0_3.png",
    "image_1_0.png",
    "image_1_1.png",
    "image_1_2.png",
    "image_1_3.png",
    "image_2_0.png",
    "image_2_1.png",
    "image_2_2.png",
    "image_2_3.png",
    "image_3_0.png",
    "image_3_1.png",
    "image_3_2.png",
    "image_3_3.png",
  ];

  const selectedCountryOption = useMemo(() => {
    const fallback =
      COUNTRY_OPTIONS.find(c => c.name === "Singapore") || COUNTRY_OPTIONS[0];

    const v = oUser?.countryCode;
    if (!v) { return fallback; }
    const byName = COUNTRY_OPTIONS.find(c => c.name === v);
    if (byName) { return byName; }
    const byCode = COUNTRY_OPTIONS.find(c => (c.code || c.value) === v);
    return byCode || fallback;
  }, [oUser?.countryCode]);

  useEffect(() => {
    console.log("oUser", oUser);
    if (oUser?.countryCode) {
      setCountryCode(oUser.countryCode);
    }
    if (oUser?.locale) {
      setLocale(oUser.locale);
    }
  }, [oUser?.countryCode, oUser?.locale]);

  useEffect(() => {
    AppHelper.apiAuditPost("P-P", "");
  }, []); 

  const getAvatarUrl = (avatarImage) => {
    if (!avatarImage) return "/profile_icon/image_0_0.png"; // fallback
    return `/profile_icon/${avatarImage}`;
  };

  useEffect(()=>{
    if(!isMyProfile) return;
    if (isMyProfile?.isLinked === true) {
      setEmail1(isMyProfile?.email1);
      setEmail2(isMyProfile?.email2);
      setGrade(isMyProfile?.grade);
    } else {
      setAccountType(oUser.accountType || 'STU');
      setEmail1(oUser.email1 || '');
      setEmail2(oUser.email2 || '');
      setGrade((oUser && oUser.grade) ? oUser.grade : 'P5');
    }
  }, [isMyProfile]);

  const openChangePictureDialog = (profile, isDefault = false) => {
    if (!profile) return;
    setUpdatedProfileId(profile.profileId);
    setIsDefaultProfileUpdated(isDefault);

    // Start with that profile’s current avatar, or the logged-in user, or fallback
    const initialAvatar =
      profile.avatarImage ||
      oUser.picture ||
      "image_0_0.png";

    setSelectedAvatar(initialAvatar);
    setIsChangingPicture(true);
  };

	const closeChangePictureDialog = () => {
	  setUpdatedProfileId('');
	  setIsChangingPicture(false);
	};

  const saveAvatarSelection = async () => {
    if (!selectedAvatar || !updatedProfileId) {
      alert('Please select an icon first.');
      return;
    }

    const res = await AppHelper.apiPost('api/profile/avatar/update', {
      token: oUser.token, 
      profileId: updatedProfileId, 
      avatarImage: selectedAvatar, 
    });

    if (!res.status) {
      alert('Failed to update profile icon: ' + res.message);
      return;
    }
    // Refresh list to update Avatar in profile picture
    await fetchProfiles(); 

    // Update user cache for app-top-bar profile image update. 
    if (isDefaultProfileUpdated) {
      const updatedUser = { ...oUser, picture: selectedAvatar };
      setOUser(updatedUser);
      AppHelper.userCacheUpdate(updatedUser);
      if (refTopBar?.current?.refreshProfile) {
        refTopBar.current.refreshProfile();
      }
    }
    setIsChangingPicture(false);
    setUpdatedProfileId('');
  };
  
  const openEditDialog = (profile) => {
	  setNewProfileName(profile.name);
	  setNewProfileGrade(profile.grade);
	  setUpdatedProfileId(profile.profileId);
	  setNewEmail1(profile.email1);
	  setNewEmail2(profile.email2);
	  setIsUpdatingProfile(true);
	};
  
  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const response = await AppHelper.apiPost('api/profile-linked/list', {token: oUser.token});
      if (response && response.data) {
		let profiles = response.data;
		profiles.forEach((x) => (x.isOwnProfile = 1));

		const responseShare = await AppHelper.apiPost('api/profile/share/list', { token: oUser.token });

		if (responseShare && responseShare.data) {
			const profileShare = responseShare.data;
			profileShare.forEach((x) => (x.isOwnProfile = 0));
			
			profiles = [...profiles, ...profileShare];
		}

    const myProfile = profiles.find(p => p.profileId === oUser.profileId);
    if (myProfile) {
      const newCountryCode = myProfile.countryCode || 'Singapore';
      if (oUser.countryCode !== newCountryCode || countryCode !== newCountryCode) {
        setCountryCode(newCountryCode);
        const updatedUser = { ...oUser, countryCode: newCountryCode };
        setOUser(updatedUser); 
        AppHelper.userCacheUpdate(updatedUser);
      }
    }
    if (myProfile.locale) {
      const newLocale = myProfile.locale;
      if (oUser.locale !== newLocale || locale !== newLocale) {
        setLocale(newLocale);
        const updatedUser = { ...oUser, locale: newLocale };
        setOUser(updatedUser); 
        AppHelper.userCacheUpdate(updatedUser);
      }
    }
		setProfiles(profiles);
	} else {
        console.error('Failed to fetch profiles:', response);
      }
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
		  setLoading(false); 
		}
  };
  
  const fetchGrades = async () => {
	 var grade = await EssayHelper.essayGradeGet(oUser.token);
	 setGradeList(grade);
  };
  
  useEffect(() => {    
    fetchProfiles();
	fetchGrades();
  }, []);
  
  const openCreateProfileDialog = () => {
    setIsCreatingProfile(true);
    setIsUpdatingProfile(false);
    setNewProfileName('');
	setNewEmail1('');
	setNewEmail2('');
    setNewProfileGrade('');
  };

  const closeCreateProfileDialog = () => {
    setIsCreatingProfile(false);
	  setIsUpdatingProfile(false);
  };
  
  const closeAccountTypeDialog = () => {    
	  setIsUpdatingAccountType(false);
  };

  const createProfile = async () => {
    if (!newProfileName && !newProfileGrade) {
      alert('Please enter a profile name and select a grade to continue. ');
      return;
    }
    if (!newProfileName) {
      alert('Please enter a profile name. ');
      return;
    }
    if (!newProfileGrade) {
      alert('Please select a grade. ');
      return;
    }
    const _res = await AppHelper.apiPost('api/profile/add', {
      grade: newProfileGrade,
      name: newProfileName,	  
	  email: oUser.email,
      token: oUser.token,
	  email1:newEmail1,
	  email2:newEmail2,
    });
    if (_res.status) {
      alert('Profile created successfully');
      setNewProfileName('');
      setNewProfileGrade('');
	  setNewEmail1('');
	  setNewEmail2('');
      closeCreateProfileDialog();
	    fetchProfiles();
    } else {
      alert('Failed to create profile: ' + _res.message);
    }
  };
  
  const handleSwitchProfile = (name, selectedProfileId) => {
    const confirmed = window.confirm("Are you sure you want to switch to profile " + name + "?");
    if (confirmed) {
      AppHelper.profileDefaultCacheSet({selectedProfileId});
      setOpenSnackBar(true);
    
		var _profile = profiles.find(p=>p.profileId == selectedProfileId);
		if(!_profile){
		  alert('Profile data not found!');
		  return;
		}
		var _oUserSwitch = {...oUser};
		_oUserSwitch.name = _profile.name;
		_oUserSwitch.profileId = _profile.profileId;
		_oUserSwitch.grade = _profile.grade;
		_oUserSwitch.picture = _profile.avatarImage;
    _oUserSwitch.avatarImage = _profile.avatarImage;
    _oUserSwitch.countryCode = _profile.countryCode;
    _oUserSwitch.locale = _profile.locale;
    if (_profile.isLinked == true) {
      _oUserSwitch.accountType = _profile.linkedRole;
    }
		AppHelper.userCacheSet(_oUserSwitch);
    loadEssayList(_oUserSwitch);
		setOUser(_oUserSwitch);
    setGrade(_oUserSwitch.grade);
    setAccountType(_oUserSwitch.accountType);
		if(refTopBar){
			refTopBar.current.refreshProfile();
		}
	}
  }

  const handleCountryChange = async (event, newValue) => {
    if (!newValue) {
      setCountryCode('Singapore'); 
      return; 
    }
    const newCode = newValue.name;
    try {
      const res = await AppHelper.apiPost('api/account/updateAccountCountry', {
        token: oUser.token,
        countryCode: newCode,
      });
      if (!res || !res.status) {
        alert('Country update failed');
        return;
      }
      setCountryCode(newCode);
      const updatedUser = { ...oUser, countryCode: newCode };
      setOUser(updatedUser);
      AppHelper.userCacheUpdate(updatedUser);
    } catch (e) {
      console.error('Error updating account country:', e);
      alert('Country update failed');
    }
  };

  const handleLocaleChange = async (event) => {
    const newLocale = event.target.value;
    try {
      const res = await AppHelper.apiPost('api/account/updateAccountLocale', {
        token: oUser.token,
        locale: newLocale,
      });
      if (!res || !res.status) {
        alert('Language locale update failed');
        return;
      }
      setLocale(newLocale);
      const updatedUser = { ...oUser, locale: newLocale };
      setOUser(updatedUser);
      AppHelper.userCacheUpdate(updatedUser);
    } catch (e) {
      console.error('Error updating account locale:', e);
      alert('Language locale update failed');
    }
  };
    
  const updateProfile = async () => {
    if (!newProfileName || !newProfileGrade) {
      alert('Please fill in all fields');
      return;
    }
    const _res = await AppHelper.apiPost('api/profile/update', {
      grade: newProfileGrade,
	  profileId:updatedProfileId,
      name: newProfileName,	  
	  email: oUser.email,
	  email1:newEmail1,
	  email2:newEmail2,
      token: oUser.token
    });
    if (!_res.status) {
      alert('Failed to create profile: ' + _res.message);
      return;
    }
    alert('Profile updated successfully');
    setNewProfileName('');
    setNewProfileGrade('');
	setEmail1('');
	setEmail2('');
    closeCreateProfileDialog();
    fetchProfiles();
  
    if(oUser.profileId == updatedProfileId){		 
      oUser.name =  newProfileName;
      oUser.grade = newProfileGrade;
	  oUser.email1 = newEmail1;
	  oUser.email2 = newEmail2;
	  setGrade(oUser.grade);
      AppHelper.userCacheUpdate(oUser);
    }
  };
  
  const updateAccountType = async () => {
    if (!accountType) {
      alert('Please fill in all fields');
      return;
    }
    const _res = await AppHelper.apiPost('api/account/updateAccountType', {
      accountType:accountType,
      token: oUser.token
    });
    if (!_res.status) {
      alert('Profile updated successfully');
      return;
    }
    oUser.accountType =  accountType;
		AppHelper.userCacheUpdate(oUser);
		setIsUpdatingAccountType(false);
  };
  
  const gradeSelect = async(arg) =>{
    setGrade(arg.target.value);
    var _res = await AppHelper.apiPost('api/profile/update/grade', {token: oUser.token, grade: arg.target.value});
    if(!_res.status){
      alert('ERROR: Update failed!');
      return;
    }
    oUser.grade = arg.target.value;
    AppHelper.userCacheSet({...oUser});
  };
  const go2Home = ()=>{
    navigate('/home');
  };
  const go2ScoreRule = ()=>{
    navigate('/essay/score/rule');
  };
  const clearBrowserCache = ()=>{
    if(window && 'caches' in window){
      caches.keys().then((names) => {
        names.forEach(name => {
            caches.delete(name);
        })
      })
    }
    window.location.reload(true);
  };

  const confirmDelete = async (profileId) => {
	  const confirmed = window.confirm("Are you sure you want to delete this profile?");
    if(!confirmed) return;
    try {
      const response = await AppHelper.apiPost('api/profile/remove', {
      profileId,
      token: oUser.token
      });
      if (response.status) {
      alert('Profile deleted successfully');
      fetchProfiles(); // Update the profiles list after deletion
      } else {
      alert('Failed to delete profile: ' + response.message);
      }
    } catch (error) {
      console.error('Error deleting profile:', error);
    }
	};
  const accountTypeSelect = async(arg)=>{
    const _res = await AppHelper.apiPost('api/account/updateAccountType', {
      accountType:arg.target.value,
      token: oUser.token
    });
    if (!_res.status) {
      alert('Account Type update failed');
      return;
    }
    setAccountType(arg.target.value);
    oUser.accountType =  arg.target.value;
		AppHelper.userCacheUpdate(oUser);
  }
  
  const onEmailChange = async(arg, type)=>{
    
	if(type == 1)
	{
		setEmail1(arg.target.value);
		oUser.email1 =  arg.target.value;
	}
	else if(type == 2)
	{
		setEmail2(arg.target.value);
		oUser.email2 =  arg.target.value;
	}
	AppHelper.userCacheUpdate(oUser);
  }
  
   const onEmailSave = async(type)=>{
    const _res = await AppHelper.apiPost('api/account/updateEmail', {
      email1:email1,
	  email2:email2,
	  profileId:oUser.profileId,
	  emailType:type,
      token: oUser.token
    });
    if (!_res.status) {
      alert('Email update failed');
      return;
    }
	else{
		 alert('Email update success');
	}
    
	if(type == 1)
		oUser.email1 =  email1;
	else if(type == 2)
		oUser.email2 =  email2;
	AppHelper.userCacheUpdate(oUser);
  }
  const handleClose = () => {
    setOpenSnackBar(false);
  }
  return (
  <div>
    <AppTopBar ref={refTopBar}/>
    <Container component="main" maxWidth="sm" sx={{ mb: 4 }}>
      <Paper sx={{ 
        my: { 
          xs: 3, md: 5 }, p: { xs: 2, md: 3 }, minHeight:{md:'45vh', xs:'70vh'}, 
          //backgroundColor:'rgb(235, 217, 180)' 
        }} elevation={3}>
        <nav aria-label="main mailbox folders">
          <List>
            <ListItem>
              <ListItemAvatar>
                <Avatar src={getAvatarUrl(isMyProfile?.avatarImage || oUser?.picture)} 
                  onClick={() => openChangePictureDialog(profiles.find(x=>x.profileId === oUser.profileId), true)} 
                  referrerPolicy="no-referrer" 
                />
            </ListItemAvatar>
            <ListItemText primary={oUser.name}  secondary={(oUser.metadata && oUser.metadata.lastSignInTime) ? oUser.metadata.lastSignInTime : '' } />

            {isMyProfile ? (
              isMyProfile?.isLinked == false ? (
              <IconButton onClick={() => openEditDialog(profiles.find(x=>x.profileId 
                === oUser.profileId))}>
                <EditIcon />
              </IconButton>
              ) : (
             <Box marginLeft="auto">
              <Chip label={'LINKED'} variant="filled" style={{
                backgroundColor: "grey",
                color: "white"
              }} />
              <Tooltip title={`Linked from ${isMyProfile.linkedName} (${isMyProfile.email})`}>
                <IconButton>
                  <InfoIcon />
                </IconButton>
              </Tooltip>
             </Box>
            )
            ) : null}
          </ListItem>
            <ListItem disablePadding>
              <ListItemButton>
                <ListItemIcon>
                  <MailOutlineIcon sx={{ml: '10px'}}/>
                </ListItemIcon>
                <ListItemText primary={isMyProfile?.email ? isMyProfile?.email : oUser.email} />
              </ListItemButton>
            </ListItem>
          </List>
        </nav>
        <Box m={.5} pt={1.5}>
          { accountType != 'TUI' && accountType != 'PAR' && gradeList && gradeList.length>0 &&
          <FormControl fullWidth error={!gradeOK}>
            <InputLabel id='lblGrade'>GRADE</InputLabel>
            <Select labelId='lblGrade'
              value={grade}
              onChange={gradeSelect}
              label="GRADE"
              disabled={isMyProfile?.isLinked === true}
            >
              {gradeList.map((item) => (
                <MenuItem key={item.v} value={item.v}>
                  {item.n}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          }
        </Box>
        <Box m={.5} pt={1.5}>
          <FormControl fullWidth error={!gradeOK}>
            <InputLabel id='lblAccType'>ACCOUNT TYPE</InputLabel>
            <Select labelId='lblAccType'
              value={accountType}
              onChange={accountTypeSelect}
              label="ACCOUNT TYPE"
              disabled={isMyProfile?.isLinked === true}
            >
              {accountTypeList.map((item) => (
                <MenuItem key={item.v} value={item.v}>
                  {item.n}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        { accountType != 'TEA' && accountType != 'TUI' &&
        <>
        <Box m={.5} pt={1.5}>  
          <TextField
          onChange={(event)=>onEmailChange(event, 1)}
            margin="dense"
            label="Student Email (Optional)"
            value={email1}
              type="email"
            disabled={isMyProfile?.isLinked === true}
            fullWidth
            InputProps={{endAdornment: <SaveIcon onClick={()=>onEmailSave(1)} sx={{cursor:'pointer'}} />}}
          />
        </Box>
        <Box m={.5} pt={1.5}>
          <TextField
          onChange={(event)=>onEmailChange(event, 2)}
            margin="dense"
            label="Parent Email (Optional)"
            value={email2}
              type="email"
            disabled={isMyProfile?.isLinked === true}
            fullWidth
            InputProps={{endAdornment: <SaveIcon onClick={()=>onEmailSave(2)} sx={{cursor:'pointer'}} />}}
          />
        </Box>
        </>
        }
        <Box m={.5} pt={1.5}>
          <Autocomplete
            options={COUNTRY_OPTIONS}
            autoHighlight
            getOptionLabel={(option) => option.name}
            value={selectedCountryOption}
            onChange={handleCountryChange}
            disabled={isMyProfile?.isLinked === true}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{ '& > span': { mr: 1, ml: 0.5 } }}
              >
                <span>{option.emoji}</span>
                {option.name}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="COUNTRY"
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'new-password',
                }}
                InputProps={{
                  ...params.InputProps, 
                  startAdornment: selectedCountryOption?.emoji ? (
                    <Box component="span" sx={{ mr: 1 }}>
                      {selectedCountryOption.emoji}
                    </Box>
                  ) : params.InputProps.startAdornment,
                }}
              />
            )}
          />
        </Box>
        <Box m={.5} pt={1.5}>
          <FormControl fullWidth>
            <InputLabel id="lblLocale">LANGUAGE</InputLabel>
            <Select
              labelId="lblLocale"
              value={locale}
              label="LANGUAGE"
              onChange={handleLocaleChange}
              disabled={isMyProfile?.isLinked === true}
            >
              {localeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box pt={1}>
          <Grid container>
            <Grid item xs={6} paddingLeft={'0.5rem'}>
              { profiles && profiles.length>1 && (
                <h3>Student Profiles</h3>
              )}
            </Grid>
            <Grid item xs={6} display={'flex'} alignItems={'center'} justifyContent={'flex-end'}>
              { accountType != 'STU' &&
              <Button className="app-btn-bg" sx={{ marginBlockEnd: '0' }} onClick={openCreateProfileDialog} color="info" variant="contained" size="large">
                + Profile
              </Button>
              }
            </Grid>
          </Grid>
          <Box>
      {loading && <CircularProgress />}
        {!loading && <List>
              {profiles.map((profile) => {
        if (profile.profileId === oUser.profileId) {
        return null; // Skip rendering if the profile email matches the current user's email
        }
        return (
        <ListItemButton key={profile.profileId} >
			<ListItemAvatar 
        onClick={profile.isOwnProfile ? () => openChangePictureDialog(profile) : undefined}>
				<Avatar
          src={getAvatarUrl(profile.avatarImage)}
          referrerPolicy="no-referrer"
        />
			</ListItemAvatar>

          <ListItemText onClick={() => handleSwitchProfile(profile.name, profile.profileId)} 
            primary={profile.name} 
            secondary={<>
              <Chip 
                label={profile.grade} 
                sx={{
                  height: '20px',
                  backgroundColor:'rgb(19, 130, 117)', 
                  color: 'rgb(255, 255, 255)', }}
                  /> 
              <Typography
                component="span"
                variant="body2"
                style={{ display: 'block' }}
              >
              {profile.isOwnProfile ? `Created ${moment(profile.createdOn).fromNow()}` : null}
              </Typography>
            </>} />
            
            <Snackbar
              anchorOrigin={{vertical: 'top', horizontal: 'center' }}
              open={openSnackBar}
              autoHideDuration={3000}
              onClose={handleClose}
              message="Profile switched successfully!"
            />
    {profile.isOwnProfile && (profile?.isLinked==false) ? (
		  <>
			<IconButton onClick={() => openEditDialog(profile)}>
			  <EditIcon />
			</IconButton>

			<IconButton onClick={() => confirmDelete(profile.profileId)}>
			  <DeleteIcon />
			</IconButton>
		  </>
      ) : 
      <Box marginLeft="auto">
        <Chip label={'LINKED'} variant="filled" style={{
          backgroundColor: "grey",
          color: "white"
        }} />
        <Tooltip title={`Linked from ${profile.linkedName} (${profile.email})`}>
          <IconButton>
            <InfoIcon />
          </IconButton>
        </Tooltip>
      </Box>
      }
        </ListItemButton>
        );
      })}
        </List>}
          </Box>
        </Box>
        {isMyProfile?.isLinked === false && 
      <Box mt={3} display={'flex'} justifyContent={'center'}>
          <Button className="app-btn-bg"
            onClick={go2ScoreRule} color="info" variant="contained" size="large">Scoring Rules</Button>
        </Box>
        }
        <Box mt={0} display={'flex'} justifyContent={'center'}>
          <Button onClick={go2Home} color="info" variant="text" size="large">Back to Home</Button>
        </Box>
        <Box display={'flex'} justifyContent={'flex-end'}>
          <IconButton onClick={clearBrowserCache} aria-label="Refresh Browser Cache" title="Refresh Browser Cache">
            <PublishedWithChangesIcon sx={{color:'#888'}} />
          </IconButton>
        </Box>
      </Paper>
    </Container>
	  <Dialog open={isCreatingProfile || isUpdatingProfile} onClose={closeCreateProfileDialog}>
      <DialogTitle>{isUpdatingProfile ? 'Update Profile' : 'Create Profile'}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          id="name"
          label="Name"
          type="text"
          fullWidth
          value={newProfileName}
          inputProps={{ maxLength: 45 }}
          onChange={(e) => setNewProfileName(e.target.value)}
        />
        <Box marginTop={2}>
          <FormControl fullWidth>
            <InputLabel id='lblGradeDialog'>Grade</InputLabel>
            { gradeList && gradeList.length>0 &&
            <Select
              labelId='lblGradeDialog'
              value={newProfileGrade}
              onChange={(e) => setNewProfileGrade(e.target.value)}
              label="Grade"
            >
              {gradeList.map((item) => (
                <MenuItem key={item.v} value={item.v}>
                  {item.n}
                </MenuItem>
              ))}
            </Select>
            }
          </FormControl>
        </Box>
      
      <Box marginTop={2}>
          <TextField
          margin="dense"
          id="email1"
          label="Student Email (Optional)"
          type="text"
          fullWidth
          value={newEmail1}
          inputProps={{ maxLength: 45 }}
          onChange={(e) => setNewEmail1(e.target.value)}
        />
        </Box>
      
      <Box marginTop={2}>
          <TextField
          margin="dense"
          id="email2"
          label="Parent Email (Optional)"
          type="text"
          fullWidth
          value={newEmail2}
          inputProps={{ maxLength: 45 }}
          onChange={(e) => setNewEmail2(e.target.value)}
        />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeCreateProfileDialog}>Cancel</Button>
        <Button
          sx={{
            backgroundColor: 'rgb(19, 130, 117)',
            // ":hover": { backgroundColor: 'rgb(157, 188, 152)' }
          }}
          color="info"
          variant="contained"
          onClick={isUpdatingProfile ? updateProfile : createProfile}
        >
          {isUpdatingProfile ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog open={isUpdatingAccountType} onClose={closeAccountTypeDialog}>
      <DialogTitle>{'Update Account Type'}</DialogTitle>
      <DialogContent>
        
        <Box marginTop={2}>
          <FormControl fullWidth>
            <InputLabel id='lblAccountType'>Account Type</InputLabel>
            <Select
              labelId='lblAccountType'
              value={accountType}
              onChange={(e) => setAccountType(e.target.value)}
              label="Account Type"
            >
              {accountTypeList.map((item) => (
                <MenuItem key={item.v} value={item.v}>
                  {item.n}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeAccountTypeDialog}>Cancel</Button>
        <Button
          sx={{
            backgroundColor: 'rgba(19,130,117,1)',
            ":hover": { backgroundColor: 'rgb(157, 188, 152)' }
          }}
          color="info"
          variant="contained"
          onClick={updateAccountType}
        >
          {'Update'}
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog
      open={isChangingPicture}
      onClose={closeChangePictureDialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Change Profile Icon</DialogTitle>
      <DialogContent>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          sx={{ mt: 1 }}
        >
          {/* Selected icon preview */}
          <Typography variant="subtitle1">Current Selection</Typography>
          <Avatar
            src={getAvatarUrl(
              selectedAvatar ||
              isMyProfile?.avatarImage ||
              oUser?.picture
            )}
            sx={{ width: 80, height: 80, mt: 1, mb: 2 }}
          />

          <Typography variant="body2" sx={{ mb: 1 }}>
            Tap an icon to choose your profile picture.
          </Typography>

          {/* Icon grid */}
          <Grid container spacing={1}>
            {AVATAR_OPTIONS.map((fileName) => (
                <Grid item key={fileName}>
                  <IconButton
                    onClick={() => setSelectedAvatar(fileName)}
                    sx={{
                      borderRadius: '50%',
                      border:
                        selectedAvatar === fileName
                          ? '2px solid rgb(19, 130, 117)'
                          : '2px solid transparent',
                      padding: 0.3,
                    }}
                  >
                    <Avatar
                      src={getAvatarUrl(fileName)}
                      sx={{ width: 50, height: 50 }}
                    />
                  </IconButton>
                </Grid>
              ))}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={closeChangePictureDialog}>Cancel</Button>
        <Button
          sx={{
            backgroundColor: 'rgba(19,130,117,1)',
            ':hover': { backgroundColor: 'rgb(157, 188, 152)' },
          }}
          variant="contained"
          onClick={saveAvatarSelection}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  </div>
  );
}
